import React from 'react';
import { Box, Typography } from '@mui/material';
import {
  amountInWordsGbp,
  amountInWordsInr,
  computePaymentInvoiceTotals,
  formatGbp,
  formatPaymentInvoiceDate,
  paymentLineAmount,
  PAYMENT_COMPANY,
} from '../utils/paymentInvoiceModelDefaults';
import { LetterPadFrame } from '../../shared/letterPad';

const ACCENT = '#7c3aed';
const ACCENT_SOFT = 'rgba(124, 58, 237, 0.08)';
const ACCENT_HEADER = 'rgba(236, 72, 153, 0.12)';
const INK = '#0f172a';
const SLATE = '#475569';
const BORDER = 'rgba(15, 23, 42, 0.1)';

function AddressBlock({ title, name, lines, accent }) {
  return (
    <Box sx={{ flex: 1, minWidth: 0, p: 2.5 }}>
      {title && (
        <Typography
          sx={{
            fontSize: '0.72rem',
            fontWeight: 800,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            color: accent,
            mb: 1,
          }}
        >
          {title}
        </Typography>
      )}
      <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', color: INK, mb: 1, lineHeight: 1.35 }}>
        {name || '—'}
      </Typography>
      {lines.filter(Boolean).map((line) => (
        <Typography key={line} sx={{ fontSize: '0.82rem', color: SLATE, lineHeight: 1.55 }}>
          {line}
        </Typography>
      ))}
    </Box>
  );
}

export default function PaymentInvoiceTemplatePreview({ model }) {
  const { subtotal, total, inrAmount } = computePaymentInvoiceTotals(model);

  return (
    <Box
      sx={{
        borderRadius: '20px',
        overflow: 'hidden',
        boxShadow: '0 28px 70px rgba(15, 23, 42, 0.14)',
        border: `1px solid ${BORDER}`,
        fontFamily: '"Inter", "Segoe UI", system-ui, sans-serif',
        color: INK,
      }}
    >
      <LetterPadFrame sx={{ borderRadius: 0, boxShadow: 'none' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 2,
            mb: 3,
            flexWrap: 'wrap',
          }}
        >
          <Box>
            <Typography
              sx={{
                fontSize: '0.72rem',
                fontWeight: 800,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                color: ACCENT,
              }}
            >
              Export &amp; service billing
            </Typography>
            <Typography sx={{ fontSize: '0.85rem', color: SLATE, mt: 0.5 }}>
              {model.companyName || PAYMENT_COMPANY.name}
            </Typography>
          </Box>

          <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
            <Typography
              sx={{
                fontFamily: '"Georgia", "Times New Roman", serif',
                fontSize: 'clamp(1.75rem, 4vw, 2.35rem)',
                fontWeight: 700,
                letterSpacing: '0.14em',
                color: INK,
                lineHeight: 1,
                mb: 1.25,
              }}
            >
              INVOICE
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.5 }}>
              <Typography sx={{ fontSize: '0.84rem', color: SLATE }}>
                <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                  Date:
                </Box>{' '}
                {formatPaymentInvoiceDate(model.issueDate)}
              </Typography>
              <Typography sx={{ fontSize: '0.84rem', color: SLATE }}>
                <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                  Invoice No:
                </Box>{' '}
                {model.invoiceNumber || '—'}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            display: 'flex',
            borderRadius: '16px',
            border: `1px solid ${BORDER}`,
            overflow: 'hidden',
            mb: 3,
            flexDirection: { xs: 'column', md: 'row' },
            boxShadow: '0 4px 20px rgba(15, 23, 42, 0.04)',
          }}
        >
          <AddressBlock
            name={model.companyName || PAYMENT_COMPANY.name}
            lines={[model.companyAddress, `GSTIN: ${model.companyGstin}`]}
            accent={ACCENT}
          />
          <Box sx={{ width: { xs: '100%', md: 1 }, bgcolor: BORDER, minHeight: { xs: 1, md: 'auto' } }} />
          <AddressBlock
            title="Bill To"
            name={model.billToName}
            lines={[model.billToAddress]}
            accent={ACCENT}
          />
        </Box>

        <Box
          sx={{
            borderRadius: '14px',
            border: `1px solid ${BORDER}`,
            overflow: 'hidden',
            mb: 2.5,
          }}
        >
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              fontSize: '0.82rem',
              '& th': {
                bgcolor: ACCENT_HEADER,
                color: INK,
                fontWeight: 700,
                textAlign: 'left',
                px: 1.5,
                py: 1.25,
                borderBottom: `1px solid ${BORDER}`,
                whiteSpace: 'nowrap',
              },
              '& td': {
                px: 1.5,
                py: 1.35,
                borderBottom: `1px solid rgba(15, 23, 42, 0.06)`,
                color: SLATE,
                verticalAlign: 'top',
              },
              '& tr:last-child td': { borderBottom: 'none' },
            }}
          >
            <Box component="thead">
              <Box component="tr">
                {['No.', 'Description', 'Duration', 'Rate (GBP/day)', 'Amount (GBP)'].map((h) => (
                  <Box
                    component="th"
                    key={h}
                    sx={{
                      textAlign: h.startsWith('Rate') || h.startsWith('Amount') ? 'right' : 'left',
                    }}
                  >
                    {h}
                  </Box>
                ))}
              </Box>
            </Box>
            <Box component="tbody">
              {(model.lineItems || []).map((item, idx) => (
                <Box component="tr" key={item.id || idx}>
                  <Box component="td">{idx + 1}.</Box>
                  <Box component="td" sx={{ color: `${INK} !important`, fontWeight: 600 }}>
                    {item.description || '—'}
                  </Box>
                  <Box component="td">
                    {item.durationDays ? `${item.durationDays} days` : '—'}
                  </Box>
                  <Box component="td" sx={{ textAlign: 'right' }}>
                    {item.ratePerDay || '—'}
                  </Box>
                  <Box
                    component="td"
                    sx={{ fontWeight: 700, color: `${INK} !important`, textAlign: 'right' }}
                  >
                    {formatGbp(paymentLineAmount(item))}
                  </Box>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2.5 }}>
          <Box
            sx={{
              minWidth: 240,
              p: 2,
              borderRadius: '14px',
              bgcolor: ACCENT_SOFT,
              border: '1px solid rgba(124, 58, 237, 0.2)',
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '0.88rem' }}>
              <span>Sub Total</span>
              <span>{formatGbp(subtotal)}</span>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, fontSize: '0.88rem' }}>
              <span>Total</span>
              <span>{formatGbp(total)}</span>
            </Box>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                pt: 1,
                mt: 0.5,
                borderTop: `1px solid ${BORDER}`,
                fontWeight: 800,
                fontSize: '1rem',
              }}
            >
              <span>Balance Due</span>
              <span>{formatGbp(total)}</span>
            </Box>
          </Box>
        </Box>

        <Box
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: '14px',
            border: `1px solid ${BORDER}`,
            bgcolor: '#fff',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: ACCENT,
              mb: 1,
            }}
          >
            Amount chargeable (in words)
          </Typography>
          <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: INK, mb: 0.5 }}>
            GBP VALUE: {amountInWordsGbp(total)}
          </Typography>
          {inrAmount > 0 && (
            <Typography sx={{ fontSize: '0.86rem', fontWeight: 700, color: INK }}>
              INR VALUE: {amountInWordsInr(inrAmount)}
            </Typography>
          )}
        </Box>

        <Box
          sx={{
            p: 2,
            mb: 2.5,
            borderRadius: '14px',
            border: `1px solid ${BORDER}`,
            bgcolor: '#fff',
          }}
        >
          <Typography
            sx={{
              fontSize: '0.72rem',
              fontWeight: 800,
              letterSpacing: '0.1em',
              textTransform: 'uppercase',
              color: ACCENT,
              mb: 1.25,
            }}
          >
            Company&apos;s Bank Details
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
              gap: 1,
            }}
          >
            {[
              ['Bank Name', model.bankName],
              ['A/c No.', model.accountNumber],
              ['Branch & IFS Code', model.branchIfsc],
              ['Swift Code', model.swiftCode],
              ['AD Code', model.adCode],
              ['IE Code', model.ieCode],
            ].map(([label, value]) => (
              <Typography key={label} sx={{ fontSize: '0.8rem', color: SLATE }}>
                <Box component="span" sx={{ fontWeight: 700, color: INK }}>
                  {label}:
                </Box>{' '}
                {value || '—'}
              </Typography>
            ))}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', pt: 2 }}>
          <Box sx={{ textAlign: 'center', minWidth: 200 }}>
            <Box sx={{ height: 56, borderBottom: `1px solid ${BORDER}`, mb: 1 }} />
            <Typography sx={{ fontSize: '0.82rem', fontWeight: 700, color: INK }}>
              {model.authorizedSignatory || 'Authorized Signatory'}
            </Typography>
          </Box>
        </Box>
      </LetterPadFrame>
    </Box>
  );
}
